import { useState } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { MessageSquare, Send, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface SupportMessage {
  id: string;
  subject: string;
  message: string;
  status: 'open' | 'closed';
  created_at: string;
  response?: string;
}

const SAMPLE_CONVERSATIONS: SupportMessage[] = [
  {
    id: '1',
    subject: 'AI Model Integration Help',
    message: 'I need assistance integrating the new language model with our existing pipeline.',
    status: 'closed',
    created_at: '2025-03-14T10:30:00Z',
    response: "I have reviewed your setup and provided step-by-step integration instructions in the attached documentation. Let me know if you need any clarification."
  },
  {
    id: '2',
    subject: 'Dashboard Performance Issues',
    message: 'The analytics dashboard is loading slowly when viewing historical data.',
    status: 'open',
    created_at: '2025-03-13T15:45:00Z'
  },
  {
    id: '3',
    subject: 'API Authentication Error',
    message: 'Getting consistent authentication errors when making API calls from the development environment.',
    status: 'closed',
    created_at: '2025-03-12T09:15:00Z',
    response: "The issue was related to an expired API key. I have generated a new key and updated your environment. Please verify the connection now."
  }
];

export function Support() {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reset form
      setSubject('');
      setMessage('');
      setSuccess(true);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-6">
              <MessageSquare className="h-6 w-6 text-blue-500 dark:text-blue-400" />
              <h2 className="ml-2 text-xl font-semibold text-gray-900 dark:text-white">Contact Support</h2>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded">
                Message sent successfully! We'll get back to you soon.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="What can we help you with?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe your issue in detail..."
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Recent Conversations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Clock className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                <h2 className="ml-2 text-xl font-semibold text-gray-900 dark:text-white">Recent Conversations</h2>
              </div>
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="space-y-4">
              {SAMPLE_CONVERSATIONS.map((conv) => (
                <div
                  key={conv.id}
                  className="border border-gray-100 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 dark:text-white">{conv.subject}</h3>
                    <span
                      className={cn(
                        "px-2.5 py-0.5 rounded-full text-xs font-medium",
                        conv.status === 'open'
                          ? "bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400"
                          : "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400"
                      )}
                    >
                      {conv.status === 'open' ? 'Open' : 'Resolved'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{conv.message}</p>
                  {conv.response && (
                    <div className="bg-blue-50 dark:bg-blue-900/50 rounded p-3 mt-2">
                      <p className="text-sm text-blue-900 dark:text-blue-300">{conv.response}</p>
                    </div>
                  )}
                  <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="h-3 w-3 mr-1" />
                    {new Date(conv.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}